import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Wallpaper, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './WallpaperRollCalculatorPage/components/CalculationForm';
import ResultsOverview from './WallpaperRollCalculatorPage/components/ResultsOverview';
import RoomVisualization from './WallpaperRollCalculatorPage/components/RoomVisualization';
import QuickAdjustmentSidebar from './WallpaperRollCalculatorPage/components/QuickAdjustmentSidebar';
import SavedCalculationsSidebar from './WallpaperRollCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './WallpaperRollCalculatorPage/components/TipsSidebar';
import InfoContent from './WallpaperRollCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Размеры комнаты
  roomLength: '5', // м
  roomWidth: '4', // м
  roomHeight: '2.7', // м
  roomPerimeter: '18', // м (вычисляемое)
  roomArea: '48.6', // м² (вычисляемое)

  // Параметры обоев
  wallpaperType: 'standard', // 'standard', 'wide', 'photoWall', 'liquid'
  rollWidth: '53', // см
  rollLength: '10.05', // м
  patternRepeat: '0', // см
  
  // Дополнительные параметры комнаты
  includeWindows: true,
  windowsCount: '1',
  windowWidth: '1.4', // м
  windowHeight: '1.5', // м

  includeDoors: true,
  doorsCount: '1',
  doorWidth: '0.9', // м
  doorHeight: '2.1', // м
  
  // Дополнительные параметры
  wastagePercent: '10', // %
  includeСeiling: false,
  glueType: 'standard', // 'standard', 'special', 'none'
  
  // Экономический расчет
  pricePerRoll: '1200', // руб.
  pricePerGlue: '350', // руб.
  includePrimeCoat: false,
  pricePerPrimeCoat: '400', // руб.
};

// Определение типов обоев
export const wallpaperTypes = [
  { id: 'standard', name: 'Стандартные бумажные', defaultWidth: 53, recommendedGlue: 'standard' },
  { id: 'wide', name: 'Флизелиновые широкие', defaultWidth: 106, recommendedGlue: 'special' },
  { id: 'vinyl', name: 'Виниловые', defaultWidth: 53, recommendedGlue: 'special' },
  { id: 'nonwoven', name: 'Флизелиновые', defaultWidth: 53, recommendedGlue: 'special' },
  { id: 'textile', name: 'Текстильные', defaultWidth: 53, recommendedGlue: 'special' },
  { id: 'photoWall', name: 'Фотообои', defaultWidth: 127, recommendedGlue: 'special' },
  { id: 'liquid', name: 'Жидкие обои', defaultWidth: 0, recommendedGlue: 'none' }
];

// Определение типов клея
export const glueTypes = [
  { id: 'standard', name: 'Стандартный клей', description: 'Для бумажных обоев' },
  { id: 'special', name: 'Усиленный клей', description: 'Для тяжелых и флизелиновых обоев' },
  { id: 'none', name: 'Без клея', description: 'Для жидких обоев или самоклеящихся' }
];

/**
 * WallpaperRollCalculatorPage Component - Калькулятор расчета количества рулонов обоев
 */
const WallpaperRollCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('wallpaper-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор обоев (рулонов) | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн калькулятор обоев: расчет необходимого количества рулонов для вашей комнаты, учет окон, дверей, подбор клея и расчет стоимости ремонта с визуализацией результата.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedWallpaperCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findWidth = (id) => {
        const found = wallpaperTypes.find(t => t.id === id);
        return found ? found.defaultWidth.toString() : initialFormData.rollWidth;
      };

      switch (subToolId) {
        case 'standard':
          newState.wallpaperType = 'standard';
          newState.rollWidth = findWidth('standard');
          newState.glueType = 'standard';
          break;
        case 'wide':
          newState.wallpaperType = 'wide';
          newState.rollWidth = findWidth('wide');
          newState.glueType = 'special';
          break;
        case 'photo-wallpaper':
          newState.wallpaperType = 'photoWall';
          newState.rollWidth = findWidth('photoWall');
          newState.glueType = 'special';
          break;
        case 'liquid':
          newState.wallpaperType = 'liquid';
          newState.glueType = 'none';
          break;
        case 'with-pattern':
          newState.patternRepeat = '64';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Комната ${formData.roomLength}×${formData.roomWidth}м, ${getWallpaperTypeName(formData.wallpaperType)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedWallpaperCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Получение имени типа обоев по id
  const getWallpaperTypeName = (typeId) => {
    const type = wallpaperTypes.find(t => t.id === typeId);
    return type ? type.name : 'Обои';
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedWallpaperCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Wallpaper className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор обоев (рулонов)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              wallpaperTypes={wallpaperTypes}
              glueTypes={glueTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <RoomVisualization 
                  roomData={{
                    length: parseFloat(formData.roomLength),
                    width: parseFloat(formData.roomWidth),
                    height: parseFloat(formData.roomHeight),
                    windows: formData.includeWindows ? 
                      Array(parseInt(formData.windowsCount)).fill({
                        width: parseFloat(formData.windowWidth),
                        height: parseFloat(formData.windowHeight)
                      }) : [],
                    doors: formData.includeDoors ?
                      Array(parseInt(formData.doorsCount)).fill({
                        width: parseFloat(formData.doorWidth),
                        height: parseFloat(formData.doorHeight)
                      }) : []
                  }}
                  rollsCount={results.rollsRequired}
                  wallpaperType={formData.wallpaperType}
                />
              </>
            )}

            {/* --- Информационный контент об обоях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              wallpaperTypes={wallpaperTypes}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar glueTypes={glueTypes} />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default WallpaperRollCalculatorPage;